import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Check, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { OAuthPlatform, OAUTH_CONNECTORS } from '../../services/oauthConnectors';
import { WorkiumLogo } from '../brand/WorkiumLogo';
interface UserAuthModalProps {
  open: boolean;
  platform: OAuthPlatform;
  onAuthorize: () => void;
  onCancel: () => void;
}
export function UserAuthModal({
  open,
  platform,
  onAuthorize,
  onCancel
}: UserAuthModalProps) {
  const [authorizing, setAuthorizing] = useState(false);
  const config = OAUTH_CONNECTORS.find(c => c.id === platform);
  if (!config) return null;
  const handleAuthorize = async () => {
    setAuthorizing(true);
    // Simulate network delay for the authorization request
    await new Promise(resolve => setTimeout(resolve, 1500));
    setAuthorizing(false);
    onAuthorize();
  };
  return <AnimatePresence data-id="element-3053">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={!authorizing ? onCancel : undefined} className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-3054" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed z-[101] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-id="element-3055">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/50" data-id="element-3056">
              <div className="flex items-center gap-2" data-id="element-3057">
                <Shield size={16} className="text-emerald-400" data-id="element-3058" />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-3059">
                  Autorización Segura
                </span>
              </div>
              <button onClick={!authorizing ? onCancel : undefined} disabled={authorizing} className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50" data-id="element-3060">
                <X size={18} data-id="element-3061" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8 flex-1 overflow-y-auto custom-scrollbar" data-id="element-3062">
              {/* Logos */}
              <div className="flex items-center justify-center gap-4 mb-8" data-id="element-3063">
                <div className="w-16 h-16 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-center shadow-inner" data-id="element-3064">
                  <WorkiumLogo size={32} data-id="element-3065" />
                </div>
                <div className="flex flex-col items-center gap-1 text-slate-600" data-id="element-3066">
                  <ArrowRight size={20} data-id="element-3067" />
                </div>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{
              backgroundColor: `${config.brandColor}15`,
              border: `1px solid ${config.brandColor}30`
            }} data-id="element-3068">
                  <div className="text-2xl font-bold" style={{
                color: config.brandColor
              }} data-id="element-3069">
                    {config.name.charAt(0)}
                  </div>
                </div>
              </div>

              <div className="text-center mb-8" data-id="element-3070">
                <h2 className="text-xl font-bold text-slate-100 mb-2" data-id="element-3071">
                  Conectar con {config.name}
                </h2>
                <p className="text-sm text-slate-400" data-id="element-3072">
                  Workium solicita acceso a tu cuenta de {config.name} para
                  sincronizar datos y habilitar integraciones.
                </p>
              </div>

              {/* Scopes */}
              <div className="bg-slate-950/50 border border-slate-800/50 rounded-xl p-5 mb-2" data-id="element-3073">
                <p className="text-xs font-semibold text-slate-300 mb-4" data-id="element-3074">
                  Workium podrá:
                </p>
                <ul className="space-y-3" data-id="element-3075">
                  {config.defaultScopes.map(scope => <li key={scope} className="flex items-start gap-3" data-id="element-3076">
                      <div className="mt-0.5 w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0" data-id="element-3077">
                        <Check size={10} className="text-emerald-400" data-id="element-3078" />
                      </div>
                      <span className="text-sm text-slate-300 leading-snug" data-id="element-3079">
                        {config.scopeDescriptions[scope] || scope}
                      </span>
                    </li>)}
                </ul>
              </div>

              <p className="text-[10px] text-slate-500 text-center mt-4" data-id="element-3080">
                Al hacer clic en Autorizar, permitís que Workium acceda a esta
                información de acuerdo con sus Términos de Servicio y Política
                de Privacidad.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-5 border-t border-slate-800 bg-slate-900/80 flex items-center gap-3" data-id="element-3081">
              <Button variant="outline" className="flex-1 justify-center border-slate-700 text-slate-300 hover:bg-slate-800" onClick={onCancel} disabled={authorizing} data-id="element-3082">
                Cancelar
              </Button>
              <Button variant="primary" className="flex-1 justify-center text-white shadow-lg" style={{
            backgroundColor: config.brandColor
          }} onClick={handleAuthorize} disabled={authorizing} data-id="element-3083">
                {authorizing ? <>
                    <Loader2 size={16} className="mr-2 animate-spin" data-id="element-3084" />
                    Autorizando...
                  </> : 'Autorizar'}
              </Button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}